/**
 * Generic free-form tag helpers shared across management modals.
 *
 * Tags are a flat `string[]`. A tag carrying the package prefix (default
 * `package:`) drives collapsible grouping in EntityListPanel and option groups
 * in pickers; non-prefixed tags are plain searchable labels.
 */

export const PACKAGE_TAG_PREFIX = 'package:';

export interface TagGroup {
  key: string;
  label: string;
}

/** Title-case a hyphenated slug: `credential-exposure` -> `Credential Exposure`. */
export function slugToLabel(slug: string): string {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** All tags carrying the given prefix. */
export function getTagsWithPrefix(tags: string[], prefix: string = PACKAGE_TAG_PREFIX): string[] {
  return tags.filter((t) => t.startsWith(prefix));
}

/** First tag carrying the given prefix, or undefined. */
export function firstTagWithPrefix(
  tags: string[],
  prefix: string = PACKAGE_TAG_PREFIX,
): string | undefined {
  return tags.find((t) => t.startsWith(prefix));
}

/** Groups a single entity's tags by prefix. Falls back to an Ungrouped bucket. */
export function groupByTagPrefix(tags: string[], prefix: string = PACKAGE_TAG_PREFIX): TagGroup[] {
  const matched = getTagsWithPrefix(tags, prefix);
  if (matched.length === 0) {
    return [{ key: '__ungrouped__', label: 'Ungrouped' }];
  }
  return matched.map((tag) => {
    const slug = tag.slice(prefix.length);
    return { key: slug, label: slugToLabel(slug) };
  });
}

/**
 * Builds a `groupBy` function for EntityListPanel that groups any
 * `{ tags: string[] }` entity by the given prefix.
 */
export function makeTagGroupBy<T extends { tags: string[] }>(
  prefix: string = PACKAGE_TAG_PREFIX,
): (entity: T) => TagGroup[] {
  return (entity: T) => groupByTagPrefix(entity.tags, prefix);
}
